/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';


import { Theme, darken, lighten, makeStyles } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


/**
 * Used to format text.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */





/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { faFileAlt } from '@fortawesome/pro-duotone-svg-icons/faFileAlt';
import { faSlash } from '@fortawesome/pro-duotone-svg-icons/faSlash';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Skeleton from '@material-ui/lab/Skeleton';
import { ALL_COUNTRIES_CODE, ALL_INSTITUTIONS_CODE, IEvent, IEventType, IEventTypeFormType, IForm, IFormType, IPatient, onlinePatientManagement, useForm } from '@ngt/online-patient-management-ui';
import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import * as Dtos from '../../api/Neon.Dtos';
import PatientErrorContext from '../../context/PatientErrorContext';
import OpmsErrorSummary from '../OpmsForm/OpmsErrorSummary';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryFormProps {
    patient: IPatient;
    eventType: IEventType;
    formType: IFormType;
    eventTypeFormType: IEventTypeFormType | null;
    event: IEvent | null;
    form: IForm | null;
    eventRepeat: number;
    formRepeat: number;
    loadingForms: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => {
    const getColor = theme.palette.type === 'light' ? darken : lighten;
    const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;
    const getBorderColor = theme.palette.type === 'light' ? lighten : darken;

    return {
        container: {
            display: 'flex',
            marginTop: -1,
        },
        item: {
            border: `1px solid ${theme.palette.grey[300]}`
        },
        questions: {
            display: 'flex',
            padding: theme.spacing(0, 2),
            borderWidth: 1,
            borderStyle: 'solid',

            borderRadius: 0,

            color: getColor(theme.palette.grey[700], 0.6),
            backgroundColor: getBackgroundColor(theme.palette.grey[700], 0.9),
            borderColor: getBorderColor(theme.palette.grey[700], 0.8),
        },
        answeredQuestions: {
            maxWidth: 40,
            minWidth: 40,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        divider: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        totalQuestions: {
            maxWidth: 40,
            minWidth: 40,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        },
        success: {
            color: getColor(theme.palette.success.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.success.main, 0.9),
            borderColor: getBorderColor(theme.palette.success.main, 0.8),

            cursor: 'default',

            '& > *': {
                color: theme.palette.success.main,
            }
        },
        note: {
            color: getColor(theme.palette.info.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
            borderColor: getBorderColor(theme.palette.info.main, 0.8),
            '& > *': {
                color: theme.palette.info.main,
            }
        },
        eligibility: {
            color: getColor(theme.palette.warning.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
            borderColor: getBorderColor(theme.palette.warning.main, 0.8),
            '& > *': {
                color: theme.palette.warning.main
            }
        },
        error: {
            color: getColor(theme.palette.error.main, 0.6),
            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
            borderColor: getBorderColor(theme.palette.error.main, 0.8),
            '& > *': {
                color: theme.palette.error.main,
            }
        },
        stratification: {
            color: '#fff',
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: theme.palette.warning.main,
            borderColor: getColor(theme.palette.warning.main, 0.1),
        },
        critical: {
            color: '#fff',
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: theme.palette.error.main,
            borderColor: getColor(theme.palette.error.main, 0.1),
        },
    }
});

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientSummaryForm: React.FunctionComponent<IPatientSummaryFormProps> = ({
    patient,
    formType,
    eventType,
    event,
    eventRepeat,
    eventTypeFormType,
    form,
    formRepeat,
    loadingForms
}) => {
    if (!patient) {
        throw new Error('Patient must be defined.');
    }

    if (!eventType) {
        throw new Error('eventType must be defined.');
    }

    if (!formType) {
        throw new Error('formType must be defined.');
    }

    if (!eventRepeat) {
        throw new Error('eventRepeat must be defined.');
    }

    if (!formRepeat) {
        throw new Error('formRepeat must be defined.');
    }

    const classes = useStyles();


    const [, validation, , , , , formActions] = useForm(formType.id as number, patient.id as number, patient.institutionId as number, event?.id ?? null, eventType.id as number, eventRepeat, formRepeat);

    const patientErrorContext = React.useContext(PatientErrorContext);

    React.useEffect(() => {
        if (validation && patientErrorContext) {
            patientErrorContext.updateResult(validation.result);
        }
    }, [validation, patientErrorContext]);

    // screening form name and code
    const formName = formType.code === 'pre-registration' && ((patient as Dtos.Patient).treatmentId !== 1 && (patient as Dtos.Patient).treatmentId !== 2) ? 'Screening Form' : formType.name;

    React.useEffect(() => {
        if (!loadingForms) {
            formActions.validate(form ?? {} as IForm);
        }

        return () => {
            formActions.clear();
        }
    }, [formActions, loadingForms, form]);

    const [expand, setExpand] = React.useState(false);

    const onClick = React.useCallback(() => {
        setExpand(!expand);
    }, [setExpand, expand])

    const params = useParams<any>();

    const country = React.useMemo(() => {
        return params[onlinePatientManagement.routeParameters.countryCode] ?? ALL_COUNTRIES_CODE
    }, [params]);

    const institutionCode = React.useMemo(() => {
        return params[onlinePatientManagement.routeParameters.institutionCode] ?? ALL_INSTITUTIONS_CODE
    }, [params]);



    const notes = React.useMemo(() => validation?.errors?.filter(e => e.type === Dtos.ErrorType.Warning), [validation?.errors]);

    const eligibilities = React.useMemo(() => validation?.errors?.filter(e => e.type === Dtos.ErrorType.Ineligible), [validation?.errors]);

    const errors = React.useMemo(() => validation?.errors?.filter(e => e.type === Dtos.ErrorType.Normal), [validation?.errors]);

    const stratifications = React.useMemo(() => validation?.errors?.filter(e => e.type === Dtos.ErrorType.StratificationFailure), [validation?.errors]);

    const criticals = React.useMemo(() => validation?.errors?.filter(e => e.type === Dtos.ErrorType.Critical), [validation?.errors]);

    const hasNotes = notes && notes.length > 0;
    const hasEligibilities = eligibilities && eligibilities.length > 0;
    const hasErrors = errors && errors.length > 0;
    const hasStratifications = stratifications && stratifications.length > 0;
    const hasCriticals = criticals && criticals.length > 0;

    return (
        <>
            <div
                className={classes.container}
            >
                <ListItem
                    button
                    className={classes.item}
                    component={Link}
                    to={`/registration/${country}/${institutionCode}/${patient.studyNumber}/${eventType.code}/${eventRepeat}/${formType.code}/${formRepeat}`}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon fixedWidth icon={faFileAlt} size="2x" />
                    </ListItemIcon>
                    <ListItemText primary={eventTypeFormType?.repeating ? `${formName} (${formRepeat})` : formName} />
                </ListItem>
                {
                    !!validation ?
                        <Button
                            disableRipple={!hasNotes && !hasEligibilities && !hasErrors && !hasStratifications && !hasCriticals}
                            className={classNames(
                                classes.questions,
                                { [classes.success]: !hasNotes && !hasEligibilities && !hasErrors && !hasStratifications && !hasCriticals },
                                { [classes.note]: hasNotes && !hasEligibilities && !hasErrors && !hasStratifications && !hasCriticals },
                                { [classes.eligibility]: hasEligibilities && !hasErrors && !hasStratifications && !hasCriticals },
                                { [classes.error]: hasErrors && !hasStratifications && !hasCriticals },
                                { [classes.stratification]: hasStratifications && !hasCriticals },
                                { [classes.critical]: hasCriticals }
                            )}
                            onClick={hasNotes || hasEligibilities || hasErrors || hasStratifications || hasCriticals ? onClick : undefined}
                        >
                            <div
                                className={classes.answeredQuestions}
                            >
                                {validation?.answeredQuestions}
                            </div>
                            <div
                                className={classes.divider}
                            >
                                <FontAwesomeIcon fixedWidth icon={faSlash} flip="horizontal" />
                            </div>
                            <div
                                className={classes.totalQuestions}
                            >
                                {validation?.totalQuestions}
                            </div>
                        </Button> :
                        <div
                            className={classes.questions}
                        >
                            <div
                                className={classes.answeredQuestions}
                            >
                                <Skeleton width={30} />
                            </div>
                            <div
                                className={classes.divider}
                            >
                                <FontAwesomeIcon fixedWidth icon={faSlash} flip="horizontal" />
                            </div>
                            <div
                                className={classes.totalQuestions}
                            >
                                <Skeleton width={30} />
                            </div>
                        </div>
                }
            </div>
            <Collapse
                in={expand}
            >
                <OpmsErrorSummary
                    validationErrors={validation?.errors}
                />
            </Collapse>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientSummaryForm;
